import type { CellData } from '../../../../common/table/TableTypes';
import getTiptapNodeTextContent from '../../../../common/tiptap/utils/jsonContentUtils/getTiptapNodeTextContent';
import type { TiptapContent } from '../../../../common/tiptap/tiptapTypes';
import type { MilanoteEditorRef } from '../../../components/editor/milanoteEditor/MilanoteEditorTypes';

import MilanoteCellEditor from './MilanoteCellEditor';

export type TiptapCellEditorContent = TiptapContent | string | null;
type MilanoteEditorRefTiptap = MilanoteEditorRef<TiptapCellEditorContent>;

const getCellInitialValue = ({ initialValue, event }: { initialValue?: string; event?: Event }): string | undefined => {
    if (initialValue !== '') return initialValue;
    if (!(event instanceof KeyboardEvent)) return initialValue;
    if (event.isComposing || event.ctrlKey || event.metaKey || event.altKey) return initialValue;
    if (event.key.length !== 1) return initialValue;

    // When starting editing with a typed character (ie not backspace, enter, etc), replace the cell content with the typed character.
    return event.key;
};

/**
 * Handsontable editor shell for V3 table cells.
 */
export default class TiptapCellEditor extends MilanoteCellEditor<TiptapCellEditorContent, TiptapCellEditorContent> {
    beginEditing(initialValue?: string, event?: Event): void {
        const nextInitialValue = getCellInitialValue({ initialValue, event });
        super.beginEditing(nextInitialValue, event);
    }

    getEditorInstance(): MilanoteEditorRefTiptap | null {
        const { cellEditorRef } = this.milanoteHot.milanoteProps;
        return (cellEditorRef?.current as unknown as MilanoteEditorRefTiptap | null) || null;
    }

    onEditorBeginEditing(initialValue?: string): void {
        this.content = initialValue ?? this.getCellPlainTextContent();
    }

    onEditorChange(content: TiptapCellEditorContent): void {
        this.content = content;
    }

    getEditorValue(): string {
        if (!this.content) return '';
        if (typeof this.content === 'string') return this.content;

        return getTiptapNodeTextContent(this.content);
    }

    onEditorFinishEditing(): void {
        const cellEditor = this.getEditorInstance();
        if (!cellEditor) return;

        this.onEditorChange(cellEditor.getEditorState());
    }

    clearEditorContent(): void {
        this.content = null;
    }

    setPlainTextContent(plainText: string, style?: string[]): void {
        const cellEditor = this.getEditorInstance();
        if (!cellEditor) {
            this.content = plainText;
            return;
        }

        cellEditor.setPlainText(plainText, style);
        this.onEditorChange(cellEditor.getEditorState() || plainText);
    }

    getPlainTextContent(): string {
        const content = this.getEditorState();
        if (!content) return '';
        if (typeof content === 'string') return content;

        return getTiptapNodeTextContent(content);
    }

    hasValidElement(): boolean {
        return !!this.getEditorInstance();
    }

    setEditorState(content: TiptapCellEditorContent): void {
        this.content = content;

        const cellEditor = this.getEditorInstance();
        cellEditor?.onChange(content);
    }

    getEditorState(): TiptapCellEditorContent {
        const cellEditor = this.getEditorInstance();
        return cellEditor?.getEditorState() || this.content;
    }

    private getCellPlainTextContent(): string {
        const cellData = (this.cellProperties as { cellData?: CellData } | undefined)?.cellData;
        if (cellData?.tiptapContent) return getTiptapNodeTextContent(cellData.tiptapContent);

        return cellData?.value || '';
    }
}
